import {assert} from 'Asserts';
import {assertNever, JSONValue} from 'util/Types';

export interface SQLColumnInterpolationInfo {
  columnName: string;
  alias: string | null;
}

export enum SQLFilterOperator {
  EQ = 'EQ',
  GTE = 'GTE',
  IN = 'IN',
  IS = 'IS',
  JSON_IN = 'JSON_IN',
  LTE = 'LTE',
}

const SQL_FILTER_OPERATOR_TO_SYMBOL: {[k in SQLFilterOperator]: string} = {
  [SQLFilterOperator.EQ]: '=',
  [SQLFilterOperator.GTE]: '>=',
  [SQLFilterOperator.IN]: 'IN',
  [SQLFilterOperator.IS]: 'IS',
  [SQLFilterOperator.JSON_IN]: '?|',
  [SQLFilterOperator.LTE]: '<=',
};

export interface SQLFilter {
  filteredColumnName: string;
  isNot: boolean;
  operator: SQLFilterOperator;
  value: JSONValue;
}

interface SQLListFilter extends SQLFilter {
  operator: SQLFilterOperator.IN | SQLFilterOperator.JSON_IN;
}

export interface SQLInterpolationInput {
  selectedTable: string;
  excludeDuplicates: boolean;
  filters: Array<SQLFilter>;
  selectedColumns: Array<SQLColumnInterpolationInfo>;
  limit?: number;
}

/**
 * Interpolate SQLColumnInterpolationInfo into the actual SQL query string.
 *
 * Has the following grammar:
 *    QUERY = 'SELECT {{DISTINCT}} {{SELECTS}} FROM {{selectedTable}} {{FILTERS}}'
 *    DISTINCT = excludeDuplicates ? 'DISTINCT' : ''
 *    SELECTS = selectedColumns.length > 0 ? '{{COLUMN_1}}, {{COLUMN_2}}, ...' : '*'
 *    COLUMN_X = '{{selectedColumns[X].name}} {{ALIAS_X}}'
 *    ALIAS_X = columns[X].alias ? 'AS "{{selectedColumns[X].alias}}"' : ''
 *
 * If filters is specified, we add this additional filter grammar:
 *    FILTER_QUERY = {{QUERY}} WHERE {{FILTER}}
 *
 * For filters using lists of values:
 *    FILTER = "{{NOT}} {{filters[0].columnName}} {{OPERATOR}} {{VALUES}}"
 *    NOT = filters[0].isNot ? "NOT" : ""
 *    OPERATOR = "?|" | "IN" (based on lookup of filters[0].operator)
 *    VALUES = "('{{filters[0].value[0]}}', '{{filters[0].value[1]}}', ...)"
 *      | "array['{{filters[0].value[0]}}', '{{filters[0].value[1]}}', ...]"
 *
 * For EQ filters:
 *    FILTER = "{{filters[0].columnName}} {{NOT}}= {{VALUE}}"
 *    NOT = "!"
 *    VALUE = filter.value if filter.value is not a string else "'{{value}}'"
 *
 * Note that this currently only supports a single filter on a text column for a limited set of operators.
 *
 * Further adds spacing to make the query easier to read.
 *
 * An example output:
 * SELECT
 *     id as "ID",
 *     name as "Name"
 * FROM entity
 *
 * With no selected columns:
 * SELECT
 *     *
 * FROM entity
 *
 * With a filter:
 * SELECT
 *     name,
 * FROM entity
 * WHERE name IN ('example1','example2')
 *
 * Expects sqlInterpolationState.selectedTable to not be null.
 */
export default function interpolateSQLQuery(sqlInterpolationState: SQLInterpolationInput): string {
  const distinct = sqlInterpolationState.excludeDuplicates ? ' DISTINCT' : '';
  const selects = interpolateSelectedColumns(sqlInterpolationState.selectedColumns);
  const filters =
    sqlInterpolationState.filters.length > 0 ? interpolateFilters(sqlInterpolationState.filters) : null;

  let query = `SELECT${distinct}
${selects}
FROM ${sqlInterpolationState.selectedTable}`;
  if (filters != null) {
    query = `${query}
WHERE
${filters}`;
  }
  if (sqlInterpolationState.limit != null) {
    query = `${query}
LIMIT ${sqlInterpolationState.limit}`;
  }
  return query;
}

export function interpolateSelectedColumns(selectedColumns: Array<SQLColumnInterpolationInfo>): string {
  return selectedColumns.length > 0
    ? selectedColumns
        .map((selectedColumn) => {
          const alias = selectedColumn.alias ? ` AS "${selectedColumn.alias}"` : '';
          return `\t"${selectedColumn.columnName}"${alias}`;
        })
        .join(',\n')
    : '\t*';
}

function isListFilter(filter: SQLFilter): filter is SQLListFilter {
  return filter.operator === SQLFilterOperator.IN || filter.operator === SQLFilterOperator.JSON_IN;
}

function interpolateListFilter(filter: SQLListFilter): string {
  assert(Array.isArray(filter.value), 'Got non-array value for list filter');

  const operator = SQL_FILTER_OPERATOR_TO_SYMBOL[filter.operator];
  const not = filter.isNot ? 'NOT ' : '';
  const valuesAsStr = filter.value.map((value) => `'${value}'`).join(', ');
  let values;

  switch (filter.operator) {
    case SQLFilterOperator.JSON_IN:
      values = `array[${valuesAsStr}]`;
      break;
    case SQLFilterOperator.IN:
      values = `(${valuesAsStr})`;
      break;
    default:
      throw new Error(`Invalid sql filter operator type ${assertNever(filter.operator)}`);
  }

  return `${not}"${filter.filteredColumnName}" ${operator} ${values}`;
}

export function interpolateFilters(filters: Array<SQLFilter>): string {
  return filters
    .map((filter, index): string => {
      let filterClause: string;

      if (isListFilter(filter)) {
        filterClause = interpolateListFilter(filter);
      } else {
        assert(!Array.isArray(filter.value), 'Got array value for non-list filter');

        switch (filter.operator) {
          case SQLFilterOperator.EQ: {
            const operator = `${filter.isNot ? '!' : ''}${SQL_FILTER_OPERATOR_TO_SYMBOL[filter.operator]}`;
            const value = typeof filter.value === 'string' ? `'${filter.value}'` : String(filter.value);
            filterClause = `"${filter.filteredColumnName}" ${operator} ${value}`;
            break;
          }
          case SQLFilterOperator.IS: {
            filterClause = `"${filter.filteredColumnName}" ${SQL_FILTER_OPERATOR_TO_SYMBOL[filter.operator]}${
              filter.isNot ? ' NOT' : ''
            } NULL`;
            break;
          }
          case SQLFilterOperator.GTE:
          case SQLFilterOperator.LTE: {
            const value = typeof filter.value === 'string' ? `'${filter.value}'` : String(filter.value);
            filterClause = `"${filter.filteredColumnName}" ${
              SQL_FILTER_OPERATOR_TO_SYMBOL[filter.operator]
            } ${value}`;
            break;
          }
          default:
            throw new Error(`Unexpected sql filter operator type ${filter.operator}`);
        }
      }

      return `\t${index === 0 ? '' : 'AND '}${filterClause}`;
    })
    .join('\n');
}
